import { useState, FormEvent } from 'react';
import { X, Send, AlertCircle, MessageSquare, Phone, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Template {
  id: string;
  name: string;
  language: string;
  status: string;
  components: any[];
}

interface SendTestMessageModalProps {
  wabaAccountId: string;
  templates: Template[];
  isOpen: boolean;
  onClose: () => void;
}

interface TemplateVariable {
  index: number;
  placeholder: string;
}

export default function SendTestMessageModal({
  wabaAccountId,
  templates,
  isOpen,
  onClose,
}: SendTestMessageModalProps) {
  const { accessToken } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Filtrar apenas templates aprovados
  const approvedTemplates = templates.filter(
    (t) => t.status === 'APPROVED' || t.status.startsWith('ACTIVE')
  );

  // Extrair variáveis do template
  const getTemplateVariables = (template: Template): TemplateVariable[] => {
    const variables: TemplateVariable[] = [];

    if (template.components) {
      template.components.forEach((component) => {
        if (component.type === 'BODY' && component.text) {
          // Extrair {{1}}, {{2}}, etc
          const matches = component.text.match(/\{\{(\d+)\}\}/g);
          if (matches) {
            matches.forEach((match: string) => {
              const index = parseInt(match.replace(/\{\{|\}\}/g, ''));
              // Tentar extrair contexto ao redor da variável
              const regex = new RegExp(`(.{0,20})\\{\\{${index}\\}\\}(.{0,20})`, 'i');
              const contextMatch = component.text.match(regex);
              let placeholder = `Variável ${index}`;
              if (contextMatch) {
                placeholder = `${contextMatch[1].trim()} [Variável ${index}] ${contextMatch[2].trim()}`.trim();
              }
              variables.push({ index, placeholder });
            });
          }
        }
      });
    }

    return variables.sort((a, b) => a.index - b.index);
  };

  const handleTemplateChange = (templateId: string) => {
    const template = approvedTemplates.find((t) => t.id === templateId);
    setSelectedTemplate(template || null);
    setTemplateParams({});
    setRecipientPhone(''); // Limpar telefone ao trocar template
    setError(null);
    setSuccess(false);
  };

  const handleParamChange = (index: number, value: string) => {
    setTemplateParams((prev) => ({
      ...prev,
      [index.toString()]: value,
    }));
  };

  // Obter código do país baseado no idioma do template
  const getCountryCodeFromLanguage = (language: string): string => {
    const languageMap: Record<string, string> = {
      pt_BR: '+55',
      pt_PT: '+351',
      en_US: '+1',
      es_ES: '+34',
      es_MX: '+52',
      // Adicione mais conforme necessário
    };
    return languageMap[language] || '+55'; // Default Brasil
  };

  const getPhonePlaceholder = (language: string): string => {
    const placeholders: Record<string, string> = {
      pt_BR: '11999999999',
      pt_PT: '912345678',
      en_US: '2025551234',
      es_ES: '612345678',
      es_MX: '5512345678',
    };
    return placeholders[language] || '11999999999';
  };

  const getPhoneHelper = (language: string): string => {
    const helpers: Record<string, string> = {
      pt_BR: 'Digite apenas DDD + número (ex: 11999999999). O código +55 será adicionado automaticamente.',
      pt_PT: 'Digite apenas o número (ex: 912345678). O código +351 será adicionado automaticamente.',
      en_US: 'Digite apenas código de área + número (ex: 2025551234). O código +1 será adicionado automaticamente.',
    };
    return helpers[language] || 'Digite apenas o número. O código do país será adicionado automaticamente.';
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTemplate || !accessToken) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Adicionar código do país baseado no idioma do template
      const countryCode = getCountryCodeFromLanguage(selectedTemplate.language);
      const fullPhoneNumber = `${countryCode}${recipientPhone}`;

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/messaging/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            wabaAccountId,
            recipientPhone: fullPhoneNumber,
            recipientName: recipientName || undefined,
            templateName: selectedTemplate.name,
            templateLanguage: selectedTemplate.language,
            templateParams: Object.keys(templateParams).length > 0 ? templateParams : undefined,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          // Reset form
          setRecipientPhone('');
          setRecipientName('');
          setTemplateParams({});
          setSelectedTemplate(null);
          setSuccess(false);
        }, 2000);
      } else {
        setError(data.message || 'Erro ao enviar mensagem');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const variables = selectedTemplate ? getTemplateVariables(selectedTemplate) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-1 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-200">
        {/* Header with Gradient */}
        <div className="relative bg-success px-6 py-5">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <Send size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Enviar Mensagem de Teste</h2>
                <p className="text-white/90 text-sm mt-0.5">
                  Teste seu template antes de disparar em massa
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
              title="Fechar"
            >
              <X size={22} className="text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <form id="test-message-form" onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Success Message */}
            {success && (
              <div className="bg-success/10 border-2 border-success/20 rounded-xl p-4 flex items-center gap-3 shadow-sm animate-in slide-in-from-top-2 duration-300">
                <div className="p-2 bg-success/10 rounded-lg">
                  <Send className="text-success" size={20} />
                </div>
                <div>
                  <p className="text-success font-semibold">Sucesso!</p>
                  <p className="text-success/80 text-sm">Mensagem enviada com sucesso</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-danger/10 border-2 border-danger/20 rounded-xl p-4 flex items-center gap-3 shadow-sm animate-in slide-in-from-top-2 duration-300">
                <div className="p-2 bg-danger/10 rounded-lg">
                  <AlertCircle className="text-danger" size={20} />
                </div>
                <div>
                  <p className="text-danger font-semibold">Erro ao enviar</p>
                  <p className="text-danger/80 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Template Selection */}
            <div className="bg-bg-2 border border-border-1 rounded-xl p-5 shadow-sm">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-1 mb-3">
                <MessageSquare size={18} className="text-accent-1" />
                Template WhatsApp
                <span className="text-danger">*</span>
              </label>
              <select
                required
                value={selectedTemplate?.id || ''}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-4 py-3 bg-bg-1 border-2 border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-success focus:border-success transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                <option value="">Selecione um template aprovado</option>
                {approvedTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.language})
                  </option>
                ))}
              </select>
              <p className="text-text-3 text-xs mt-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-success"></span>
                Apenas templates aprovados pelo WhatsApp estão disponíveis
              </p>
            </div>

            {/* Recipient Phone */}
            <div className="bg-bg-2 border border-border-1 rounded-xl p-5 shadow-sm">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-1 mb-3">
                <Phone size={18} className="text-success" />
                Número do Destinatário
                <span className="text-danger">*</span>
              </label>
              {selectedTemplate && (
                <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-success/10 border border-success/20 rounded-lg">
                  <span className="text-sm font-mono font-semibold text-success">
                    {getCountryCodeFromLanguage(selectedTemplate.language)}
                  </span>
                  <span className="text-xs text-success/80">Código do país (automático)</span>
                </div>
              )}
              <input
                type="tel"
                required
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={selectedTemplate ? getPhonePlaceholder(selectedTemplate.language) : '11999999999'}
                minLength={10}
                className="w-full px-4 py-3 bg-bg-1 border-2 border-border-1 rounded-lg text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-success focus:border-success transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-lg"
                disabled={isLoading || !selectedTemplate}
              />
              <p className="text-text-3 text-xs mt-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-accent-1"></span>
                {selectedTemplate
                  ? getPhoneHelper(selectedTemplate.language)
                  : 'Selecione um template primeiro'}
              </p>
            </div>

            {/* Recipient Name (Optional) */}
            <div className="bg-bg-2 border border-border-1 rounded-xl p-5 shadow-sm">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-1 mb-3">
                <User size={18} className="text-accent-2" />
                Nome do Destinatário
                <span className="text-text-3 text-xs font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="João Silva"
                className="w-full px-4 py-3 bg-bg-1 border-2 border-border-1 rounded-lg text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-2 focus:border-accent-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              />
              <p className="text-text-3 text-xs mt-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-accent-2"></span>
                Para fins de rastreamento e identificação
              </p>
            </div>

            {/* Template Variables */}
            {variables.length > 0 && (
              <div className="bg-accent-2/10 border-2 border-accent-2/20 rounded-xl p-5 shadow-sm">
                <label className="flex items-center gap-2 text-sm font-semibold text-text-1 mb-4">
                  <div className="p-1.5 bg-accent-2/10 rounded-lg">
                    <MessageSquare size={16} className="text-accent-2" />
                  </div>
                  Variáveis do Template
                  <span className="ml-auto text-xs font-normal px-2 py-1 bg-accent-2/10 text-accent-2 rounded-full">
                    {variables.length} variáve{variables.length > 1 ? 'is' : 'l'}
                  </span>
                </label>
                <div className="space-y-3">
                  {variables.map((variable, idx) => (
                    <div key={variable.index} className="bg-bg-1 rounded-lg p-3 border border-accent-2/20">
                      <label className="flex items-center gap-2 text-xs font-semibold text-accent-2 mb-2">
                        <span className="flex items-center justify-center w-6 h-6 bg-accent-2/10 rounded-full text-xs">
                          {idx + 1}
                        </span>
                        Variável {variable.index}
                      </label>
                      <input
                        type="text"
                        value={templateParams[variable.index.toString()] || ''}
                        onChange={(e) => handleParamChange(variable.index, e.target.value)}
                        placeholder={variable.placeholder}
                        className="w-full px-4 py-2.5 bg-bg-1 border-2 border-border-1 rounded-lg text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-2 focus:border-accent-2 transition-all duration-200 disabled:opacity-50"
                        disabled={isLoading}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

          </form>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-bg-1 border-t border-border-1 px-6 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-bg-3 hover:bg-bg-3/80 text-text-2 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="test-message-form"
              disabled={isLoading || !selectedTemplate}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-success hover:bg-success/90 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Send size={18} />
              {isLoading ? 'Enviando...' : 'Enviar Mensagem'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
